import React, { useMemo } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import styled from "styled-components/macro";
import { fmtDate, fmtPaValue, fmtResult, isPaParameter } from "./format";

const TableScroll = styled.div`
  max-height: 60vh;
  overflow: auto;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 13px;

  th,
  td {
    padding: 6px 10px;
    text-align: left;
    border-bottom: 1px solid #eee;
    vertical-align: top;
  }

  thead th {
    position: sticky;
    top: 0;
    background: ${({ theme }) => theme.palette.background.paper};
    font-weight: 600;
    border-bottom: 2px solid #ddd;
    z-index: 1;
  }

  tbody tr:nth-of-type(even) {
    background: #fafafa;
  }
`;

const fmtRowDate = (v) =>
  fmtDate(v, { fallback: "", pattern: "yyyy-MM-dd HH:mm" });
// Prefer the exactly-as-typed value (string, trailing zeros intact) over
// the float mirror.
const fmtRowNum = (r) =>
  fmtResult(r.result_value_entered ?? r.result_value, { fallback: "" });

const WqRecordsDialog = ({
  open,
  onClose,
  wellNdx,
  parameterNdx,
  summary,
  parameters,
  processed,
  onEditEvent,
}) => {
  // Derive the records and the title metadata from the raw datasets so the
  // page stays small. Computations are skipped when the dialog is closed.
  const { records, wellName, parameterLabel, unitAbbrev } = useMemo(() => {
    if (!open || wellNdx == null || parameterNdx == null) {
      return { records: [], wellName: "", parameterLabel: "", unitAbbrev: "" };
    }
    const filtered = processed
      .filter(
        (r) => r.well_ndx === wellNdx && r.aqsci_parameter_ndx === parameterNdx
      )
      .sort(
        (a, b) => new Date(b.collect_timestamp) - new Date(a.collect_timestamp)
      );
    const well = summary?.find((s) => s.well_ndx === wellNdx);
    const param = parameters?.find(
      (p) => p.aqsci_parameter_ndx === parameterNdx
    );
    return {
      records: filtered,
      wellName: well?.well_name || "",
      parameterLabel:
        param?.parameter_name || param?.parameter_abbrev || "Parameter",
      unitAbbrev: filtered[0]?.unit_abbrev || "",
    };
  }, [open, wellNdx, parameterNdx, processed, summary, parameters]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box>
          <Typography variant="h6">{parameterLabel}</Typography>
          <Typography variant="body2" color="textSecondary">
            {wellName}
            {unitAbbrev ? ` · units: ${unitAbbrev}` : ""}
            {records.length > 0 ? ` · ${records.length} records` : ""}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {records.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No records to display.
          </Typography>
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Result</th>
                  <th>ND?</th>
                  <th>Equipment</th>
                  <th>Lab / Other</th>
                  <th>Sampled By</th>
                  <th>Source</th>
                  <th>Notes</th>
                  {onEditEvent && <th />}
                </tr>
              </thead>
              <tbody>
                {records.map((r) => {
                  // Historical rows (data_ndx >= 900_000_000) live in
                  // data_wq_historical and aren't editable.
                  const editable = r.data_ndx < 900000000;
                  const isPa = isPaParameter(r.aqsci_parameter_ndx);
                  return (
                    <tr key={r.data_ndx}>
                      <td>
                        {fmtRowDate(r.collect_timestamp)}
                        {r.edited_on && (
                          <Box
                            component="span"
                            display="block"
                            style={{
                              fontSize: 11,
                              color: "#888",
                              fontStyle: "italic",
                            }}
                          >
                            edited{" "}
                            {fmtDate(r.edited_on, {
                              pattern: "yyyy-MM-dd",
                            })}
                            {r.edited_by ? ` · ${r.edited_by}` : ""}
                          </Box>
                        )}
                      </td>
                      <td>
                        {isPa
                          ? fmtPaValue(r.result_value, { fallback: "" })
                          : `${fmtRowNum(r)} ${r.unit_abbrev || ""}`.trim()}
                      </td>
                      <td>
                        {r.is_nondetect && !isPa ? (
                          <Chip
                            size="small"
                            label="ND"
                            style={{ height: 18, fontSize: 10 }}
                          />
                        ) : (
                          ""
                        )}
                      </td>
                      <td>{r.equipment || ""}</td>
                      <td>{r.equip_lab_other || ""}</td>
                      <td>{r.sampled_by || ""}</td>
                      <td>{r.data_source || ""}</td>
                      <td style={{ whiteSpace: "pre-wrap" }}>
                        {r.notes || ""}
                      </td>
                      {onEditEvent && (
                        <td>
                          <Tooltip
                            title={
                              editable
                                ? "Edit this sampling event"
                                : "Historical record — not editable from this app"
                            }
                          >
                            {/* span so Tooltip works on a disabled IconButton */}
                            <span>
                              <IconButton
                                size="small"
                                disabled={!editable}
                                onClick={() => {
                                  if (!editable) return;
                                  onEditEvent(r);
                                  onClose();
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WqRecordsDialog;
